/* eslint-disable no-mixed-spaces-and-tabs */
import React from "react";
import styled from "styled-components";

import laptop from "@ingka/ssr-icon/paths/laptop";
import { ListViewItem } from "@ingka/list-view";
import Tooltip from "@ingka/tooltip";
import Button from "@ingka/button";
import plusSmall from "@ingka/ssr-icon/paths/plus-small";
import touchScreen from "@ingka/ssr-icon/paths/hand-touch";
import handStrikethrough from "@ingka/ssr-icon/paths/hand-strikethrough";
import trashCan from "@ingka/ssr-icon/paths/trash-can";
import { colourNeutral3 } from "@ingka/variables/colours-css";

import Modal, { ModalBody, Sheets } from "@ingka/modal";

import FlagsRow from "../../FLagsRow";
import { StyledCommercialMessage, StyledListView, StyledModalHeader } from "../../styles";
import { UserApplication } from "../../../store";
import { UserPermissions } from "../../../utils/constants";
import { isUserHasMarkets } from "../../../utils/common";
import RequestNewMarketAccess from "../../RequestNewMarketAccess";
import RequestNewRoleModal from "../../RequestNewRoleModal";

interface UserAppOverviewPropsLayout {
	application?: UserApplication & { id: string };
	onClickRequestMarkets: () => void;
	requestMarketsModal: boolean;
	setRequestMarketsModal: (val: boolean) => void;
	showRequestNewRoleModal: boolean;
	setShowRequestNewRoleModal: (val: boolean) => void;
	onClickCancelMarketRequest: () => void;
	onClickCancelRoleRequest: () => void;
	cancellingMarkets: boolean;
	cancellingRoles: boolean;
	setToast?: (val: string) => void;
}

const TextWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
`;

const MarketsWrapper = styled.div`
	border-top: 1px ${colourNeutral3} solid;
	margin-top: 1rem;
`;

const StyledButton = styled(Button)`
	flex: 0 50%;
`;

const StyledListViewItem = styled(ListViewItem)`
	flex: 1.6 1 0%;
`;

const RequestedMarketsTitleWrapper = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
`;

const PermissionsActionsWrapper = styled.div`
	margin-top: 0.5rem;
	display: flex;
	gap: 0.5rem;
`;

const Layout: React.FC<UserAppOverviewPropsLayout> = ({
	application,
	onClickRequestMarkets,
	requestMarketsModal,
	setRequestMarketsModal,
	setToast,
	showRequestNewRoleModal,
	setShowRequestNewRoleModal,
	onClickCancelMarketRequest,
	cancellingMarkets,
	onClickCancelRoleRequest,
	cancellingRoles,
}) => {
	return (
		<>
			{application && (
				<>
					<h3>Application</h3>
					<StyledListView showDivider={false} id="user-application-overview">
						<StyledListViewItem
							leadingIcon={laptop}
							emphasised
							showDivider={false}
							description={
								<>
									Role:{" "}
									{application.permissions === "admin" ? (
										<StyledCommercialMessage message="Admin" variant="family" subtle />
									) : application.permissions === UserPermissions.Maintainer &&
									  !Object.keys(application?.markets || {}).length ? (
										<StyledCommercialMessage message="Read Only" variant="new" subtle />
									) : (
										application.permissions === UserPermissions.Maintainer &&
										isUserHasMarkets(application.markets) && (
											<StyledCommercialMessage message="Maintainer" variant="nlp" subtle />
										)
									)}
								</>
							}
							addOn={
								<>
									{application?.requestedPermissions && (
										<>
											Requested Role:{" "}
											{application?.requestedPermissions === "admin" ? (
												<StyledCommercialMessage message="Admin" variant="family" subtle />
											) : application?.requestedPermissions === UserPermissions.ReadOnly ? (
												<StyledCommercialMessage message="Read Only" variant="new" subtle />
											) : (
												application?.requestedPermissions === UserPermissions.Maintainer && (
													<StyledCommercialMessage message="Maintainer" variant="nlp" subtle />
												)
											)}
										</>
									)}
									<PermissionsActionsWrapper>
										<StyledButton
											size="small"
											id="change-role-button"
											type="emphasised"
											text="Change Role"
											ssrIcon={touchScreen}
											onClick={() => setShowRequestNewRoleModal(true)}
											disabled={cancellingRoles}
											data-autofocus
											aria-haspopup="dialog"
										/>
										{application?.requestedPermissions && (
											<StyledButton
												size="small"
												id="cancel-change-role-button"
												text="Cancel Change Role"
												ssrIcon={handStrikethrough}
												onClick={onClickCancelRoleRequest}
												loading={cancellingRoles}
											/>
										)}
									</PermissionsActionsWrapper>
								</>
							}
							controlIcon="chevronRightSmall"
							title={application?.applicationName}
						/>
					</StyledListView>
					{application.permissions === UserPermissions.Maintainer && isUserHasMarkets(application.markets) && (
						<>
							<MarketsWrapper>
								<RequestedMarketsTitleWrapper>
									<TextWrapper>
										<h3>Markets assigned for this application</h3>
									</TextWrapper>
									<div>
										<Tooltip interactiveCustomTrigger tooltipText="Request Markets">
											<Button
												id="request-markets-button"
												text="Request market access for this application"
												ssrIcon={plusSmall}
												iconOnly
												type="emphasised"
												size="small"
												onClick={onClickRequestMarkets}
												aria-haspopup="dialog"
											/>
										</Tooltip>
									</div>
								</RequestedMarketsTitleWrapper>
								<FlagsRow
									markets={Object.keys(application?.markets || {})}
									size={125}
									tooltipBoundaryContainerID="user-application-overview"
								/>
							</MarketsWrapper>
							<MarketsWrapper>
								<RequestedMarketsTitleWrapper>
									<TextWrapper>
										<h3>Markets requested for this application</h3>
									</TextWrapper>
									{application?.requestedMarkets?.length && (
										<Tooltip interactiveCustomTrigger tooltipText="Remove Request">
											<Button
												id="cancel-markets-request-button"
												ssrIcon={trashCan}
												type="emphasised"
												text="Remove markets access request"
												size="small"
												iconOnly
												loading={cancellingMarkets}
												onClick={onClickCancelMarketRequest}
											/>
										</Tooltip>
									)}
								</RequestedMarketsTitleWrapper>
								<FlagsRow
									markets={application?.requestedMarkets ? application?.requestedMarkets : []}
									size={125}
									tooltipBoundaryContainerID="user-application-overview"
								/>
							</MarketsWrapper>
						</>
					)}
				</>
			)}
			{application?.id && (
				<Modal visible={requestMarketsModal} handleCloseBtn={() => setRequestMarketsModal(false)}>
					<Sheets
						id="request-markets-modal"
						aria-labelledby="requestMarketModalTitle"
						footer={null}
						header={
							<StyledModalHeader
								title={`Request Markets in ${application?.applicationName}`}
								backBtnClick={() => setRequestMarketsModal(false)}
								titleId="requestMarketModalTitle"
							/>
						}
					>
						<ModalBody>
							<RequestNewMarketAccess
								appId={application?.id}
								onModalClose={() => setRequestMarketsModal(false)}
								setToast={setToast}
							/>
						</ModalBody>
					</Sheets>
				</Modal>
			)}
			{application && (
				<RequestNewRoleModal
					app={application}
					showRequestNewRoleModal={showRequestNewRoleModal}
					setShowRequestNewRoleModal={setShowRequestNewRoleModal}
				/>
			)}
		</>
	);
};

export default Layout;
